import { useState } from "react";
import { useIntl } from "react-intl";

import { alpha, Box, darken, Link, Typography, useTheme } from "@mui/material";

import CustomIcon from "src/components/custom-icon";

import { FolderOpen } from '@mui/icons-material';


// Limite de caracteres para a descrição da seção
const DESCRIPTION_MAX_LENGTH = 150;

const stripHtml = (html) => {
  // Substitui tags de quebra de linha por \n antes de remover o HTML
  let processedHtml = html
    .replace(/<br\s*\/?>/gi, '\n')
  const doc = new DOMParser().parseFromString(processedHtml, 'text/html');
  let text = doc.body.textContent || '';

  // Normaliza quebras de linha: remove múltiplas quebras consecutivas (mantém máximo de 2)
  text = text
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return text;
};

export default function NewsSectionCardListHeader({ clientportal, sectionInfo, articles }) {
  const intl = useIntl();
  const theme = useTheme();

  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

  const primaryColor = theme.palette.primary.main;

  const accentColor = clientportal.defaultcolor || primaryColor;

  const backgroundHeaderSection = clientportal.defaultcolor
    ? `linear-gradient(135deg, ${clientportal.defaultcolor} 0%, ${darken(clientportal.defaultcolor, 0.15)} 100%)`
    : `linear-gradient(135deg, ${primaryColor} 0%, ${darken(primaryColor, 0.15)} 100%)`;

  function renderSubtitle() {
    // { Descrição da seção }
    if (!!sectionInfo?.description) {
      const fullText = stripHtml(sectionInfo.description);
      const isLongText = fullText.length > DESCRIPTION_MAX_LENGTH;
      const displayText = isLongText && !isDescriptionExpanded
        ? `${fullText.substring(0, DESCRIPTION_MAX_LENGTH)}...`
        : fullText;

      return (
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.85)',
              mt: 0.5,
              fontSize: { xs: '0.8rem', md: '0.875rem' },
              display: 'inline',
              whiteSpace: 'pre-line'
            }}
          >
            {displayText}
          </Typography>

          {isLongText && (
            <Link
              component="button"
              variant="body2"
              onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
              sx={{
                color: 'rgba(255, 255, 255, 1) !important',
                ml: 0.5,
                fontSize: { xs: '0.8rem', md: '0.875rem' },
                fontWeight: 600,
                textDecoration: 'underline',
                cursor: 'pointer',
                '&:hover': {
                  color: 'rgba(255, 255, 255, 0.9) !important',
                }
              }}
            >
              {isDescriptionExpanded
                ? intl.formatMessage({ id: "see.less" })
                : intl.formatMessage({ id: "see.more" })
              }
            </Link>
          )}
        </Box>
      );
    }

    {/* Contador de artigos */ }
    if (articles && articles.length > 0) {
      return (
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.85)',
            mt: 0.5,
            fontSize: { xs: '0.8rem', md: '0.875rem' }
          }}
        >
          {articles.length} {articles.length === 1
            ? intl.formatMessage({ id: "news.singular" }).toLowerCase() || 'notícia'
            : intl.formatMessage({ id: "news" }).toLowerCase() || 'notícias'
          }
        </Typography>
      )

    }

    return null;
  }

  return (
    <Box sx={{
      background: backgroundHeaderSection,
      px: { xs: 2.5, sm: 3, md: 4 },
      py: { xs: 2.5, sm: 3 },
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Elemento decorativo de fundo */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 120,
          height: 120,
          borderRadius: '50%',
          backgroundColor: alpha(accentColor, 1.5),
          pointerEvents: 'none'
        }}
      />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
        {/* Ícone da seção */}
        <Box sx={{
          width: { xs: 44, md: 52 },
          height: { xs: 44, md: 52 },
          borderRadius: '12px',
          backgroundColor: alpha(theme.palette.text.secondary, 0.45),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backdropFilter: 'blur(10px)',
        }}>
          <CustomIcon icon={FolderOpen} color={"#fff"} iconSx={{ fontSize: { xs: 24, md: 28 } }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h5"
            sx={{
              color: '#fff',
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.4rem', md: '1.6rem' },
              lineHeight: 1.3,
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
          >
            {!!sectionInfo
              ? sectionInfo?.name
              : intl.formatMessage({ id: "news.section" })}
          </Typography>

          {/* Subtítulo da seção */}
          {renderSubtitle()}
        </Box>
      </Box>
    </Box>
  )
}